import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useState, useEffect } from "react";
import { useScrollAnimation, fadeInUp, AnimatedDiv, AnimatedP } from "@/hooks/useScrollAnimation";
import { motion, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet-async";

import slideshowInterior from "@/assets/st-george-luxury-home-interior-design.webp";
import slideshowInterior960 from "@/assets/st-george-luxury-home-interior-design-960w.webp";
import slideshowInterior1440 from "@/assets/st-george-luxury-home-interior-design-1440w.webp";
import slideshowInteriorAvif from "@/assets/st-george-luxury-home-interior-design.avif";
import slideshowInteriorAvif960 from "@/assets/st-george-luxury-home-interior-design-960w.avif";
import slideshowInteriorAvif1440 from "@/assets/st-george-luxury-home-interior-design-1440w.avif";
import slideshowPool from "@/assets/st-george-luxury-home-pool-resort-living.webp";
import slideshowPool960 from "@/assets/st-george-luxury-home-pool-resort-living-960w.webp";
import slideshowPool1440 from "@/assets/st-george-luxury-home-pool-resort-living-1440w.webp";
import slideshowGolf from "@/assets/st-george-golf-course-community-homes.webp";
import stGeorgeLuxuryHome from "@/assets/st-george-utah-luxury-desert-home.webp";

const slides = [
  { image: slideshowInterior, title: "MODERN LUXURY INTERIORS", alt: "St. George Utah luxury home modern interior design" },
  { image: slideshowPool, srcSet: `${slideshowPool960} 960w, ${slideshowPool1440} 1440w, ${slideshowPool} 1920w`, title: "RESORT-STYLE LIVING", alt: "St. George Utah luxury home with resort-style pool and patio" },
  { image: slideshowGolf, title: "GOLF COURSE COMMUNITIES", alt: "St. George Utah golf course community homes for sale" },
  { image: stGeorgeLuxuryHome, title: "DESERT LUXURY LIVING", alt: "St. George Utah luxury desert home with red rock views" },
];

const HeroBottom = () => {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const { ref, controls } = useScrollAnimation();

  // Auto-advance slides
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !email.includes("@")) return;
    
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      setEmail("");
      setSubmitted(true);
      setTimeout(() => setSubmitted(false), 4000);
    }, 1000);
  };

  return (
    <>
      <Helmet />

    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Background Slideshow */}
      <div className="absolute inset-0">
        {/* First slide rendered as static img for instant LCP */}
        {currentSlide === 0 ? (
          <picture>
            <source
              type="image/avif"
              srcSet={`${slideshowInteriorAvif960} 960w, ${slideshowInteriorAvif1440} 1440w, ${slideshowInteriorAvif} 1920w`}
              sizes="100vw"
            />
            <img
              src={slides[0].image}
              srcSet={`${slideshowInterior960} 960w, ${slideshowInterior1440} 1440w, ${slideshowInterior} 1920w`}
              sizes="100vw"
              alt={slides[0].alt}
              className="w-full h-full object-cover"
              width={1920}
              height={1080}
              loading="lazy"
              decoding="async"
            />
          </picture>

        ) : (
          <AnimatePresence mode="wait">
            <motion.img
              key={currentSlide}
              src={slides[currentSlide].image}
              srcSet={slides[currentSlide].srcSet}
              sizes="100vw"
              alt={slides[currentSlide].alt}
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover"
              initial={{ opacity: 0, scale: 1.1 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1.2, ease: "easeInOut" }}
              width={1920}
              height={1080}
            />
          </AnimatePresence>
        )}
        <div className="hero-overlay absolute inset-0"></div>
      </div>

      {/* Slide Indicators */}
      <div className="absolute bottom-32 left-1/2 transform -translate-x-1/2 z-20 flex gap-3">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`w-3 h-3 rounded-full transition-all duration-300 ${
              index === currentSlide 
                ? "bg-primary w-8" 
                : "bg-white/50 hover:bg-white/80"
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>

      {/* Content */}
      <AnimatedDiv 
        ref={ref}
        initial="hidden"
        animate={controls}
        variants={fadeInUp}
        className="relative z-10 text-center max-w-4xl mx-auto px-6"
      >
        <AnimatePresence mode="wait">
          <motion.h2
            key={currentSlide}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.6 }}
            className="text-4xl md:text-6xl lg:text-7xl heading-luxury text-white mb-6"
          >
            {slides[currentSlide].title}
          </motion.h2>
        </AnimatePresence>

        {/* Subtitle - centered below headline on mobile only */}
        <AnimatedP 
          variants={fadeInUp}
          className="block md:hidden text-sm text-white mt-[20px] text-center leading-relaxed"
        >
          The latest in St. George luxury homes, lifestyle & desert living, curated just for you.
        </AnimatedP>
      </AnimatedDiv>

      {/* Bottom Left Content */}
      <AnimatedDiv 
        initial="hidden"
        animate={controls}
        variants={fadeInUp}
        className="absolute bottom-16 left-6 right-6 md:right-auto z-10 max-w-md"
      >
        {/* Subtitle - bottom-left on desktop only */}
        <AnimatedP 
          variants={fadeInUp}
          className="hidden md:block text-lg text-white mb-6 text-left leading-relaxed"
        >
          The latest in St. George luxury homes, lifestyle & desert living, curated just for you.
        </AnimatedP>
        
        <form onSubmit={handleSubmit} className="flex gap-3">
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Enter your email"
            className="bg-white/10 border-white/30 text-white placeholder:text-white/60 focus:border-primary backdrop-blur-sm"
            required
          />
          <Button 
            type="submit"
            disabled={isSubmitting}
            className="btn-luxury px-6 whitespace-nowrap"
          >
            {isSubmitting ? "JOINING..." : "JOIN"}
          </Button>
        </form>
        <AnimatePresence>
          {submitted && (
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="text-primary text-sm mt-3 font-medium"
            >
              ✓ You're in! We'll keep you updated with the latest listings.
            </motion.p>
          )}
        </AnimatePresence>
      </AnimatedDiv>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 right-6 transform">
        <div className="w-px h-16 bg-primary/50 relative">
          <div className="w-2 h-2 bg-primary rounded-full absolute -left-0.5 top-0 animate-pulse"></div>
        </div>
      </div>
    </section>
    </>
  );
};

export default HeroBottom;
